import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

// hooks and utils
import { searchFilterOrders } from "../utils/searchFilterOrders";
import { ORDER_FULFILLMENT, ORDER_PAYMENT } from "@/utils/statusStyles";
import { formatDateTime } from "@/utils/formatDateTime";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchOrdersList } from "@/services/ordersApi";
import { useBusiness } from "@hooks/useBusiness";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";
import { useStorePath } from "@hooks/useStorePath";

const resolveStatusStyle = (map, status) => {
  const fallback = map?.unknown ?? {
    label: "Unknown",
    color: "bg-[#F3F4F6] text-[#4B5563]",
  };

  if (!status) return fallback;

  if (!map) return fallback;

  const normalized = status.toLowerCase().replace(/\s+/g, "");
  const entry = Object.entries(map).find(
    ([key]) => key.toLowerCase().replace(/\s+/g, "") === normalized
  );

  return entry ? map[entry[0]] : fallback;
};

const SortedIndicator = ({ state }) => {
  if (state === "asc") return <span className="text-[10px]">ASC</span>;
  if (state === "desc") return <span className="text-[10px]">DESC</span>;
  return null;
};

export default function useOrdersTable() {
  const navigate = useNavigate();
  const { storePath } = useStorePath();
  const { businessId, users } = useBusiness();
  
  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get("search") || "";

  const updateSearchTerm = useCallback(
    (val) => {
      const params = new URLSearchParams(searchParams);
      if (val) {
        params.set("search", val);
      } else {
        params.delete("search");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const orderStatusFilter = searchParams.get("orderStatus") || "All";
  const salesChannelFilter = searchParams.get("channel") || "All";

  const updateOrderStatusFilter = useCallback(
    (val) => {
      const params = new URLSearchParams(searchParams);
      if (val && val !== "All") {
        params.set("orderStatus", val);
      } else {
        params.delete("orderStatus");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const updateSalesChannelFilter = useCallback(
    (val) => {
      const params = new URLSearchParams(searchParams);
      if (val && val !== "All") {
        params.set("channel", val);
      } else {
        params.delete("channel");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const {
    data: orders = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // Must be wrapped: passing fetchOrdersList bare hands react-query's context
    // object in as the first argument, where businessId is expected.
    queryKey: ["orders", businessId, Object.keys(users).length],
    queryFn: () => fetchOrdersList(businessId, users),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const filteredOrders = useMemo(
    () => searchFilterOrders(orders, searchTerm, orderStatusFilter, salesChannelFilter),
    [orders, searchTerm, orderStatusFilter, salesChannelFilter]
  );

  const handleView = useCallback(
    (order) => {
      if (!order?.id) {
        console.error("Missing order ID");
        return;
      }
      navigate(storePath(`orders/${order.id}`));
    },
    [navigate]
  );

  const orderColumns = useMemo(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            className="rounded"
            checked={table.getIsAllPageRowsSelected()}
            onChange={(e) => table.toggleAllPageRowsSelected(!!e.target.checked)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="rounded"
            checked={row.getIsSelected()}
            onChange={(e) => row.toggleSelected(!!e.target.checked)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "orderNumber",
        header: "Order Number",
        cell: ({ row }) => row.original.orderNumber || "-",
        sortingFn: "alphanumeric",
        enableSorting: true,
      },
      {
        accessorKey: "orderDate",
        header: ({ column }) => (
          <button
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Order Date
            <SortedIndicator state={column.getIsSorted()} />
          </button>
        ),
        cell: ({ row }) => {
          const value = row.original.orderDate;
          return <span title={value}>{formatDateTime(value)}</span>;
        },
        sortingFn: "datetime",
        enableSorting: true,
      },
      {
        accessorKey: "customer.name",
        header: "Customer",
        cell: ({ row }) => row.original.customer?.name || "-",
        sortingFn: "alphanumeric",
        enableSorting: true,
      },
      {
        accessorKey: "fulfillmentStatus",
        header: "Fulfillment",
        cell: ({ row }) => {
          const style = resolveStatusStyle(ORDER_FULFILLMENT, row.original.fulfillmentStatus);
          return (
            <span className={`px-2 py-1 rounded-md text-xs font-medium ${style.color}`}>
              {style.label}
            </span>
          );
        },
      },
      {
        accessorKey: "paymentStatus",
        header: "Payment",
        cell: ({ row }) => {
          const style = resolveStatusStyle(ORDER_PAYMENT, row.original.paymentStatus);
          return (
            <span className={`px-2 py-1 rounded-md text-xs font-medium ${style.color}`}>
              {style.label}
            </span>
          );
        },
      },
      {
        accessorKey: "pricing.total",
        header: ({ column }) => (
          <button
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Total
            <SortedIndicator state={column.getIsSorted()} />
          </button>
        ),
        cell: ({ row }) => formatCurrency(row.original.pricing?.total),
        sortingFn: "basic",
        enableSorting: true,
      },
      {
        accessorKey: "meta.totalQuantity",
        header: "Items",
        cell: ({ row }) => row.original.meta?.totalQuantity ?? row.original.items ?? 0,
        sortingFn: "basic",
        enableSorting: true,
      },
    ],
    []
  );

  const renderOrderRow = useCallback(
    (row) => {
      const order = row.original;
      return (
        <TableRow
          key={row.id}
          onClick={() => handleView(order)}
          className="cursor-pointer hover:bg-[#f6f6ff] transition-colors"
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
              {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
            </TableCell>
          ))}
        </TableRow>
      );
    },
    [handleView]
  );

  return {
    allOrders: orders,
    orders: filteredOrders,
    searchTerm,
    updateSearchTerm,
    orderStatusFilter,
    updateOrderStatusFilter,
    salesChannelFilter,
    updateSalesChannelFilter,
    refetch,
    orderColumns,
    renderOrderRow,
    isLoading,
    isError,
  };
}

